/*
 * Copyright (c) 2026
 */
import { useEffect, useRef, useState } from 'react'

/** Min widths (px), aligned with the web app Chakra theme breakpoints. */
const WIDTHS = {
  sm: 480,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const

const ORDERED_KEYS = ['sm', 'md', 'lg', 'xl', '2xl'] as const

export type HolidayBreakpointMap<T> = { base: T } & Partial<{
  sm: T
  md: T
  lg: T
  xl: T
  '2xl': T
}>

function resolveFromWidth<T>(map: HolidayBreakpointMap<T>, width: number): T {
  let bestMin = 0
  let result: T = map.base

  for (const key of ORDERED_KEYS) {
    const min = WIDTHS[key]
    if (width >= min && map[key] !== undefined && min >= bestMin) {
      bestMin = min
      result = map[key] as T
    }
  }

  return result
}

/**
 * Mobile-first responsive values (sm 480px → 2xl 1536px).
 * SSR / first paint uses `base`; after mount, matches viewport width.
 */
export function useHolidayResponsiveValue<T>(map: HolidayBreakpointMap<T>): T {
  const mapRef = useRef(map)
  mapRef.current = map

  const [value, setValue] = useState<T>(() => map.base)

  useEffect(() => {
    const update = () => {
      setValue(resolveFromWidth(mapRef.current, window.innerWidth))
    }
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  return value
}
